/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';

import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Auth } from 'aws-amplify';
import { Spinner } from 'react-bootstrap';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Table from 'react-bootstrap/Table';
import { Link, useNavigate } from 'react-router-dom';
import { useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import { formatIsoToDate } from '../../utils';
import checklistApi from '../../utils/checklistAPI';
import Button from '../Button';
import TableFilter from '../ChecklistTable/CommonTableFilter';
import TablePagination from '../ChecklistTable/CommonTablePagination';

function getSortIcon(isSorted, isSortedDesc) {
  let iconProp = faSort;
  if (isSorted) {
    iconProp = isSortedDesc ? faSortDown : faSortUp;
  }
  return <FontAwesomeIcon icon={iconProp} className="ml-1 fa-sm" />;
}

// Client column: 'All' for CLD Default, AMC name for AMC, "id - name" for a single client.
function clientCell(instance) {
  const row = instance?.row?.original;
  if (row?.qualLevel === 'CLD Default') return 'All';
  if (row?.qualLevel === 'AMC') return row?.amcName || row?.accountMasterCode || '';
  return row?.clientId ? `${row.clientId} - ${row.clientName || ''}` : '';
}

// CP-836 — AI Client Qualification list table. Cloned from ChecklistTable (the DQF list idiom:
// react-table + shared filter/pagination + bootstrap Table). Columns per mockup page 25.
export default function QualificationTable({ data }) {
  const columnClassName = 'align-middle text-nowrap';
  const memoData = React.useMemo(() => data, [data]);
  const [loading, setLoading] = useState(false);
  const skipPageResetRef = React.useRef();
  const navigation = useNavigate();

  const columns = React.useMemo(
    () => [
      {
        Header: 'ID',
        accessor: 'id',
        className: columnClassName,
        Cell: instance => <Link to={`/qualification-list/edit-details/${instance.value}`}>{instance.value}</Link>,
      },
      {
        Header: 'Category',
        accessor: 'category',
        className: columnClassName,
        Cell: () => 'Qualifications',
      },
      {
        Header: 'Level',
        accessor: 'qualLevel',
        className: columnClassName,
        Cell: instance => (instance.value === 'Single Client' ? 'Client Specific' : instance.value),
      },
      {
        Header: 'Client',
        accessor: 'clientId',
        className: columnClassName,
        Cell: clientCell,
      },
      {
        Header: 'Created',
        accessor: 'createdAt',
        className: columnClassName,
        Cell: instance => formatIsoToDate(instance.value),
      },
      {
        Header: 'Last Updated',
        accessor: 'updatedAt',
        className: columnClassName,
        Cell: instance => formatIsoToDate(instance.value),
      },
    ],
    [],
  );

  const tableInstance = useTable(
    {
      columns,
      data: memoData,
      initialState: {
        sortBy: [{ id: 'id', desc: true }],
      },
      autoResetPage: !skipPageResetRef.current,
      autoResetExpanded: !skipPageResetRef.current,
      autoResetGroupBy: !skipPageResetRef.current,
      autoResetSelectedRows: !skipPageResetRef.current,
      autoResetSortBy: !skipPageResetRef.current,
      autoResetFilters: !skipPageResetRef.current,
      autoResetRowState: !skipPageResetRef.current,
      autoResetGlobalFilter: !skipPageResetRef.current,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow, setFilter, setGlobalFilter } =
    tableInstance;

  const tableHeaders = headerGroups.map(headerGroup => {
    const headers = headerGroup.headers.map(column => (
      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
        {column.render('Header')}
        <span>{column.canSort ? getSortIcon(column.isSorted, column.isSortedDesc) : null}</span>
      </th>
    ));
    return <tr {...headerGroup.getHeaderGroupProps()}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const cells = row.cells.map(cell => (
      <td {...cell.getCellProps({ className: cell.column.className, style: cell.column.style })}>
        {cell.render('Cell')}
      </td>
    ));
    return <tr {...row.getRowProps()}>{cells}</tr>;
  });

  function filter(event, columnName) {
    const filterColumn = columns.find(column => column.Header === columnName);
    if (filterColumn) {
      setFilter(filterColumn.accessor, event.target.value);
    } else {
      setGlobalFilter(event.target.value);
    }
  }

  // Allocate a new qualification ID from the unique-id service (same pattern as checklists'
  // /dqf-chklst-ids). NOTE: the /dqf-qual-ids endpoint is a required dqf-apiv2 addition (backend).
  const getNewId = async () => {
    setLoading(true);
    const response = await checklistApi.get('/dqf-qual-ids', {
      headers: {
        Authorization: (await Auth.currentSession()).getIdToken().getJwtToken(),
      },
    });
    setLoading(false);
    if (response.data?.response_body?.id) {
      navigation(`/qualification-list/details/${response.data.response_body.id}`);
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <TableFilter columns={columns} onSearchChange={filter} />
        </Col>
        <Col className="d-flex justify-content-center">
          <TablePagination tableInstance={tableInstance} />
        </Col>
        <Col className="text-right">
          <Button className="px-5" variant="primary" onPress={getNewId}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Add'}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col className="border border-light rounded shadow-sm">
          <Table hover responsive borderless {...getTableProps()}>
            <thead className="text-nowrap">{tableHeaders}</thead>
            <tbody {...getTableBodyProps()}>{tableRows}</tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}
